import { z } from "zod";

import {
  EventCategorySchema,
  EventSchema,
  EventSession,
  EventSessionSchema,
  EventSessionStatusSchema,
  EventStatsSchema,
  EventStatusSchema,
  LocationSchema,
  OrgSchema,
  TicketTier,
  TicketTierSchema,
  TicketTierStatusSchema
} from "../../models";
import { PageReqSchema } from "../base";
import { EventQuestionDtoSchema } from "./question";

export * from "./stats";
export * from "./question";
export * as EventCardDto from "./card";

// Event category list item (for category filter)
export const EventCategoryListItemSchema = EventCategorySchema.pick({
  id: true,
  name: true
});
export type EventCategoryListItem = z.infer<typeof EventCategoryListItemSchema>;

// Event session with detail
export const EventSessionWithDetailSchema = EventSessionSchema.extend({
  ticketTiers: z.array(TicketTierSchema)
});
export type EventSessionWithDetail = z.infer<typeof EventSessionWithDetailSchema>;

// Event with detail
export const EventWithDetailSchema = EventSchema.extend({
  host: OrgSchema,
  location: LocationSchema.optional().nullable(),
  stats: EventStatsSchema,
  sessions: z.array(EventSessionWithDetailSchema),
  category: EventCategorySchema.optional().nullable(),
  booked: z.boolean().optional(),
  questions: z.array(EventQuestionDtoSchema).default([])
});
export type EventWithDetail = z.infer<typeof EventWithDetailSchema>;

// Create event schema
export const CreateEventSchema = EventSchema.omit({
  id: true,
  slug: true,
  categoryId: true,
  featured: true,
  isPublic: true,
  status: true,
  createdAt: true,
  updatedAt: true
}).extend({
  isPublic: z.boolean().default(true),
  sessions: z.array(
    EventSessionSchema.omit({
      id: true,
      eventId: true,
      taken: true,
      createdAt: true,
      updatedAt: true
    }).extend({
      ticketTiers: z.array(
        TicketTierSchema.omit({
          id: true,
          eventId: true,
          eventSessionId: true,
          taken: true,
          status: true,
          createdAt: true,
          updatedAt: true
        })
      )
    })
  )
});
export type CreateEvent = z.infer<typeof CreateEventSchema>;

// Update event schema - restricted to specific fields only
export const UpdateEventSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  banner: z.string().url().optional(),
  featuredImages: z.array(z.string().url()).optional(),
  categoryId: z.string().optional().nullable(),
  isPublic: z.boolean().optional()
});
export type UpdateEvent = z.infer<typeof UpdateEventSchema>;

// Status update schemas
export const UpdateEventStatusSchema = z.object({
  status: EventStatusSchema
});
export type UpdateEventStatus = z.infer<typeof UpdateEventStatusSchema>;

export const UpdateSessionStatusSchema = z.object({
  status: EventSessionStatusSchema
});
export type UpdateSessionStatus = z.infer<typeof UpdateSessionStatusSchema>;

export const UpdateTicketTierStatusSchema = z.object({
  status: TicketTierStatusSchema
});
export type UpdateTicketTierStatus = z.infer<typeof UpdateTicketTierStatusSchema>;

// Query events request schema
export const EventSortBySchema = z.enum([
  "nextSessionAt",
  "createdAt",
  "title",
  "ticketsSold",
  "revenueGross"
]);
export type EventSortBy = z.infer<typeof EventSortBySchema>;

export const QueryEventsReqSchema = PageReqSchema.extend({
  // Filters
  categoryId: z.array(z.string()).optional(),
  hostId: z.string().optional(),
  featured: z.coerce.boolean().optional(),
  status: z.array(EventStatusSchema).optional(),
  search: z.string().optional(),
  startFrom: z.coerce.date().optional(),
  startTo: z.coerce.date().optional(),
  // Sorting
  sortBy: EventSortBySchema.optional(),
  sortOrder: z.enum(["asc", "desc"]).optional()
});
export type QueryEventsReq = z.infer<typeof QueryEventsReqSchema>;

// Query event sessions request schema
export const EventSessionQuerySchema = z.object({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional()
});
export type EventSessionQuery = z.infer<typeof EventSessionQuerySchema>;

// Create event session schema
export const CreateEventSessionSchema = EventSessionSchema.omit({
  id: true,
  eventId: true,
  taken: true,
  createdAt: true,
  updatedAt: true
})
  .extend({
    ticketTiers: z.array(
      TicketTierSchema.omit({
        id: true,
        eventId: true,
        eventSessionId: true,
        taken: true,
        status: true,
        createdAt: true,
        updatedAt: true
      })
    )
  })
  .superRefine(({ startAt, endAt }, ctx) => {
    const now = new Date();
    const oneDayLater = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    // rule 1: startAt >= now + 1 day
    if (startAt < oneDayLater) {
      ctx.addIssue({
        path: ["startAt"],
        code: z.ZodIssueCode.custom,
        message: "Start time must be at least 1 day in the future"
      });
    }

    // rule 2: endAt (if exists) > startAt
    if (endAt !== null && endAt <= startAt) {
      ctx.addIssue({
        path: ["endAt"],
        code: z.ZodIssueCode.custom,
        message: "End time must be later than start time"
      });
    }
  });
export type CreateEventSession = z.infer<typeof CreateEventSessionSchema>;

// ------------------------------------------------------------------------------------------------
export function remainTicketAmount(eventSession: EventSession, ticketTier: TicketTier): number {
  return Math.min(eventSession.cap - eventSession.taken, ticketTier.cap - ticketTier.taken);
}
